import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import Icon from '../../components/AppIcon';

import { useAuth } from '../../contexts/AuthContext';
import { transferDealService, subscribeToDeals } from '../../services/transferMarketplaceService';
import DealKanban from './components/DealKanban';
import DealAnalytics from './components/DealAnalytics';
import DealDetailsModal from './components/DealDetailsModal';
import { supabase } from '../../lib/supabase';

const DealTrackingDashboard = () => {
  const { user, userProfile } = useAuth();
  const [deals, setDeals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('kanban');
  const [selectedDeal, setSelectedDeal] = useState(null);
  const [updateLoading, setUpdateLoading] = useState(false);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    loadDeals();
    
    const channel = subscribeToDeals(() => {
      loadDeals();
    });

    return () => {
      if (channel) supabase?.removeChannel(channel);
    };
  }, []);

  const loadDeals = async () => {
    try {
      setLoading(true);
      const { data } = await transferDealService?.getAll();
      setDeals(data || []);
    } catch (error) {
      console.error('Error loading deals:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateStage = async (dealId, newStage) => {
    try {
      setUpdateLoading(true);
      await transferDealService?.update(dealId, { currentStage: newStage });
      loadDeals();
      if (selectedDeal?.id === dealId) {
        const { data } = await transferDealService?.getById(dealId);
        setSelectedDeal(data);
      }
    } catch (error) {
      console.error('Error updating deal stage:', error);
      alert(error?.message || 'Failed to update deal stage');
    } finally {
      setUpdateLoading(false);
    }
  };

  const filteredDeals = deals?.filter(deal => {
    if (filter === 'all') return true;
    if (filter === 'active') return !['completed', 'cancelled']?.includes(deal?.currentStage);
    if (filter === 'urgent') return deal?.isUrgent;
    if (filter === 'overdue') {
      if (!deal?.nextDeadline) return false;
      return new Date(deal?.nextDeadline) < new Date();
    }
    return true;
  });

  return (
    <>
      <Helmet>
        <title>Deal Tracking Dashboard - ProX11Market</title>
        <meta name="description" content="Comprehensive deal management dashboard for tracking transfer negotiations and transactions" />
      </Helmet>
      <div className="min-h-screen bg-background">
        <Header />
        <main className="pt-20 pb-12">
          <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8">
            {/* Header */}
            <div className="mb-8">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h1 className="text-3xl md:text-4xl font-bold text-foreground mb-2">Deal Tracking Dashboard</h1>
                  <p className="text-muted-foreground">Monitor and manage all active transfer negotiations</p>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => setViewMode('kanban')}
                    className={`px-4 py-2 rounded-lg flex items-center gap-2 transition-colors ${
                      viewMode === 'kanban' ?'bg-primary text-primary-foreground' :'bg-muted text-muted-foreground hover:bg-muted/80'
                    }`}
                  >
                    <Icon name="LayoutGrid" size={18} />
                    Kanban
                  </button>
                  <button
                    onClick={() => setViewMode('analytics')}
                    className={`px-4 py-2 rounded-lg flex items-center gap-2 transition-colors ${
                      viewMode === 'analytics' ?'bg-primary text-primary-foreground' :'bg-muted text-muted-foreground hover:bg-muted/80'
                    }`}
                  >
                    <Icon name="BarChart" size={18} />
                    Analytics
                  </button>
                </div>
              </div>

              {/* Filters */}
              <div className="flex gap-2">
                {['all', 'active', 'urgent', 'overdue']?.map(filterOption => (
                  <button
                    key={filterOption}
                    onClick={() => setFilter(filterOption)}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                      filter === filterOption
                        ? 'bg-primary text-primary-foreground'
                        : 'bg-muted text-muted-foreground hover:bg-muted/80'
                    }`}
                  >
                    {filterOption?.charAt(0)?.toUpperCase() + filterOption?.slice(1)}
                  </button>
                ))}
              </div>
            </div>

            {/* Analytics View */}
            {viewMode === 'analytics' && (
              <div className="space-y-6">
                <DealAnalytics deals={filteredDeals} />
                
                <div className="bg-card border border-border rounded-lg p-6">
                  <h2 className="text-xl font-bold text-foreground mb-4">Recent Deals</h2>
                  <div className="space-y-3">
                    {filteredDeals?.slice(0, 5)?.map(deal => (
                      <div
                        key={deal?.id}
                        className="flex items-center justify-between p-4 bg-muted/50 rounded-lg hover:bg-muted transition-colors cursor-pointer"
                        onClick={() => setSelectedDeal(deal)}
                      >
                        <div className="flex items-center gap-4">
                          <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center">
                            <Icon name="Briefcase" size={20} color="var(--color-primary)" />
                          </div>
                          <div>
                            <div className="font-medium text-foreground">{deal?.dealTitle}</div>
                            <div className="text-sm text-muted-foreground">{deal?.player?.fullName} - €{deal?.proposedFee}M</div>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <span className="text-sm text-muted-foreground capitalize">{deal?.currentStage?.replace('_', ' ')}</span>
                          <Icon name="ChevronRight" size={20} color="var(--color-muted-foreground)" />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Kanban View */}
            {viewMode === 'kanban' && (
              <div>
                {loading ? (
                  <div className="flex items-center justify-center py-12">
                    <div className="text-center">
                      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
                      <p className="text-muted-foreground">Loading deals...</p>
                    </div>
                  </div>
                ) : filteredDeals?.length === 0 ? (
                  <div className="text-center py-12">
                    <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
                      <Icon name="Briefcase" size={32} color="var(--color-muted-foreground)" />
                    </div>
                    <h3 className="text-lg font-semibold text-foreground mb-2">No deals found</h3>
                    <p className="text-muted-foreground mb-4">Start by responding to transfer inquiries in the marketplace</p>
                  </div>
                ) : (
                  <DealKanban
                    deals={filteredDeals}
                    onViewDetails={(deal) => setSelectedDeal(deal)}
                    onUpdateStage={handleUpdateStage}
                  />
                )}
              </div>
            )}
          </div>
        </main>
      </div>

      {/* Deal Details Modal */}
      {selectedDeal && (
        <DealDetailsModal
          deal={selectedDeal}
          onClose={() => setSelectedDeal(null)}
          onUpdateStage={handleUpdateStage}
          loading={updateLoading}
        />
      )}
    </>
  );
};

export default DealTrackingDashboard;